import { Fragment, useState } from "react";
import { fl, srcLabel } from "@/lib/finI18n";
import { fmt2 } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { DrillNum, useDrill } from "@/components/reports/Drill";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronRight } from "lucide-react";

const n = (v) => fmt2(v || 0);
const BK = ["b0", "b30", "b60", "b90"];
const BK_TONE = { b0: "text-emerald-700", b30: "text-amber-600", b60: "text-orange-600", b90: "text-rose-600" };

function Buckets({ report, lang, ids }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="arap-buckets">
      <Card className="p-3">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{fl("totalOutstanding", lang)}</p>
        <DrillNum value={report.totals.closing} q={{ account: ids, basis: "cumulative" }} title={fl("totalOutstanding", lang)} testid="arap-total-closing" className="text-lg font-bold" />
      </Card>
      {report.buckets.map((b) => (
        <Card key={b.key} className="p-3" data-testid={`arap-bucket-${b.key}`}>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{b.label} {fl("days", lang)}</p>
          <p className={`num text-lg font-semibold ${BK_TONE[b.key]}`}>{n(b.amount)}</p>
        </Card>
      ))}
    </div>
  );
}

function Items({ p, lang, expand }) {
  const drill = useDrill();
  const items = expand ? p.items : p.items.filter((it) => Math.abs(it.outstanding) > 0.004);
  return (
    <TableRow className="bg-secondary/20 hover:bg-secondary/20">
      <TableCell colSpan={10} className="py-2 pl-8">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="h-8">{fl("docNo", lang)}</TableHead><TableHead className="h-8">{fl("date", lang)}</TableHead><TableHead className="h-8">{fl("due", lang)}</TableHead>
            <TableHead className="h-8 text-right">{fl("age", lang)}</TableHead><TableHead className="h-8">{fl("desc", lang)}</TableHead>
            <TableHead className="h-8 text-right">{fl("value", lang)}</TableHead><TableHead className="h-8 text-right">{fl("settled", lang)}</TableHead><TableHead className="h-8 text-right">{fl("outstanding", lang)}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-xs text-muted-foreground">—</TableCell></TableRow>}
            {items.map((it, i) => (
              <TableRow key={i} className={it.doc_id ? "cursor-pointer" : ""} data-testid={`arap-item-${i}`}
                onClick={() => it.doc_id && drill && drill({ doc: it.doc_id, basis: "cumulative", title: `${it.doc_no || srcLabel(it.source, lang)} · ${p.party}` })}>
                <TableCell className="text-xs">{it.doc_no || "—"} {it.source && <Badge variant="outline" className="ml-1 text-[10px]">{srcLabel(it.source, lang)}</Badge>}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{fmtDate(it.date)}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{fmtDate(it.due_date) || "—"}</TableCell>
                <TableCell className={`num text-right text-xs ${BK_TONE[it.bucket]}`}>{it.days}</TableCell>
                <TableCell className="text-xs max-w-[280px]">{it.description}</TableCell>
                <TableCell className="num text-right text-xs">{n(it.amount)}</TableCell>
                <TableCell className="num text-right text-xs">{n(it.paid)}</TableCell>
                <TableCell className="num text-right text-xs font-semibold">{n(it.outstanding)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCell>
    </TableRow>
  );
}

function PartyRow({ p, i, ids, lang, open, onToggle }) {
  const q = (basis) => ({ account: ids, party: p.party, basis });
  return (
    <TableRow className="cursor-pointer" onClick={onToggle} data-testid={`arap-party-${i}`}>
      <TableCell className="text-sm font-medium"><ChevronRight className={`inline h-3.5 w-3.5 mr-1 transition-transform ${open ? "rotate-90" : ""}`} />{p.party}
        {p.open_count > 0 && <span className="ml-2 text-[11px] text-muted-foreground">{p.open_count} {fl("openDocs", lang)}</span>}</TableCell>
      <TableCell className="num text-right text-xs">{n(p.opening)}</TableCell>
      <TableCell className="text-right text-xs"><DrillNum value={p.increase} q={q("period")} title={`${fl("increase", lang)} · ${p.party}`} testid={`arap-inc-${i}`} /></TableCell>
      <TableCell className="text-right text-xs"><DrillNum value={p.decrease} q={q("period")} title={`${fl("decrease", lang)} · ${p.party}`} testid={`arap-dec-${i}`} /></TableCell>
      <TableCell className="text-right text-xs font-semibold"><DrillNum value={p.closing} q={q("cumulative")} title={`${fl("ending", lang)} · ${p.party}`} testid={`arap-closing-${i}`} /></TableCell>
      {BK.map((k) => <TableCell key={k} className={`num text-right text-xs ${p.buckets[k] ? BK_TONE[k] : "text-muted-foreground"}`}>{p.buckets[k] ? n(p.buckets[k]) : "—"}</TableCell>)}
    </TableRow>
  );
}

export function ARAPView({ report, mode, expand, lang, kind }) {
  const [toggled, setToggled] = useState({});
  const ids = report.account_ids.join(",");
  const T = report.totals;
  return (
    <div className="space-y-4" data-testid={`${kind}-view`}>
      <Buckets report={report} lang={lang} ids={ids} />
      <Card className="p-4 overflow-auto">
        <p className="text-xs text-muted-foreground mb-2">{fl("agingAsOf", lang)} {report.as_of}</p>
        <Table>
          <TableHeader><TableRow>
            <TableHead>{fl(kind === "ar" ? "customer" : "vendor", lang)}</TableHead><TableHead className="text-right">{fl("opening", lang)}</TableHead>
            <TableHead className="text-right">{fl("increase", lang)}</TableHead><TableHead className="text-right">{fl("decrease", lang)}</TableHead>
            <TableHead className="text-right">{fl("ending", lang)}</TableHead>
            {report.buckets.map((b) => <TableHead key={b.key} className="text-right whitespace-nowrap">{b.label}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {report.parties.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-sm text-muted-foreground" data-testid="report-empty">{fl("empty", lang)}</TableCell></TableRow>}
            {report.parties.map((p, i) => {
              const open = toggled[i] ?? mode !== "summary";
              return (
                <Fragment key={p.party}>
                  <PartyRow p={p} i={i} ids={ids} lang={lang} open={open} onToggle={() => setToggled({ ...toggled, [i]: !open })} />
                  {open && <Items p={p} lang={lang} expand={expand} />}
                </Fragment>
              );
            })}
            <TableRow className="bg-secondary/60 font-semibold">
              <TableCell>{fl("total", lang)}</TableCell>
              <TableCell className="num text-right">{n(T.opening)}</TableCell><TableCell className="num text-right">{n(T.increase)}</TableCell>
              <TableCell className="num text-right">{n(T.decrease)}</TableCell><TableCell className="num text-right" data-testid="arap-grand-closing">{n(T.closing)}</TableCell>
              {BK.map((k) => <TableCell key={k} className="num text-right">{n(T[k])}</TableCell>)}
            </TableRow>
          </TableBody>
        </Table>
        <p className="text-[11px] text-muted-foreground pt-3">{fl(kind === "ar" ? "arNote" : "apNote", lang)}</p>
      </Card>
    </div>
  );
}
